/**
 * CH Map Filter Module
 *
 * Maintains a single google maps instance that can be used to filter our browse results
 *
 */
define('ch_map_filter',[
    'jquery',
    'google_maps_new',
    'overlay_popup',
    'services',
    'semantic',
    'ch_ga'
], function ($, gmaps, OverlayPopup) {

    /**
     * reference to the google map instance
     */
    var map;

    /**
     * array of markers added to the map instance
     * @type {Array}
     */
    var markers = [];

    /**
     * array of "custom overlays" added to the map instance
     * @type {Array}
     */
    var overlays = [];

    /**
     * options
     */
    var options = {
        defaultZoomLevel: 12,
        defaultCenter: null,
        // user defined callback to trigger any time map bounds or zoom changes
        mapChangeCallback: function() {},
        overlayClickCallback: function() {},
        markerClickCallback: function() {}
    }

    /**
     * Track a user event
     *
     * Wrap ga() to curry in a few args
     *
     * @param eventType
     * @param nonInteraction
     */
    function trackEvent(action, nonInteraction) {
        var labels = [];
        action = typeof action !== 'undefined' ? action : 'generic';

        if (CH) {
            if (CH.Hub && CH.Hub.uri) {
                labels.push('Hub:' + CH.Hub.uri);
            }
            if (CH.category && CH.category.name) {
                labels.push('Cat:' + CH.category.name);
            }
        }
        label = labels.join(' | ');

        nonInteraction = typeof nonInteraction !== 'undefined' ? nonInteraction : false;
        ga('send', 'event', 'Filter Map', action, label, {
            nonInteraction: nonInteraction,
        });
    }

    /**
     * Activate a marker for a given record
     * @param recordId
     */
    function activateMarkerForRecord(recordId) {
        var markersForCourse = markers.filter(function(marker) {
            return marker.recordId == recordId;
        });
        markersForCourse.forEach(function(marker) {
            marker.infoWindow.open(map, marker.marker);
            map.panTo(marker.marker.position);
        });
    };

    /**
     * Blur a marker for a given record
     * @param recordId
     */
    function deactivateMarkerForRecord(recordId) {
        var markersForCourse = markers.filter(function(marker) {
            return marker.recordId == recordId;
        });
        markersForCourse.forEach(function(marker) {
            marker.infoWindow.close();
        });
    };

    /**
     * Activate an overlay for a given record
     * @param recordId
     */
    function activateOverlayForRecord(recordId) {
        var overlaysForRecord = overlays.filter(function(overlay) {
            return overlay.recordId == recordId;
        });
        overlaysForRecord.forEach(function(overlay) {
            overlay.overlay.setActive();
        });
    };

    /**
     * Blur an overlay for a given record
     * @param recordId
     */
    function deactivateOverlayForRecord(recordId) {
        var overlaysForRecord = overlays.filter(function(overlay) {
            return overlay.recordId == recordId;
        });
        overlaysForRecord.forEach(function(overlay) {
            overlay.overlay.setInactive();
        });
    };

    /**
     * Show an overlay for a given record
     * @param recordId
     */
    function showOverlayForRecord(recordId) {
        var overlaysForRecord = overlays.filter(function(overlay) {
            return overlay.recordId == recordId;
        });
        overlaysForRecord.forEach(function(overlay) {
            overlay.overlay.show();
        });
    };

    /**
     * Hide an overlay for a given record
     * @param recordId
     */
    function hideOverlayForRecord(recordId) {
        var overlaysForRecord = overlays.filter(function(overlay) {
            return overlay.recordId == recordId;
        });
        overlaysForRecord.forEach(function(overlay) {
            overlay.overlay.hide();
        });
    };

    /**
     * add markers to map for given courses
     *
     * @param courses
     */
    function mapCourses(courses) {
        for (var i = 0; i < courses.length; i++) {
            if (
                courses[i] &&
                courses[i].location &&
                courses[i].location.latitude &&
                courses[i].location.longitude &&
                courses[i].provider
            ) {
                addMarker(
                    courses[i].location.latitude,
                    courses[i].location.longitude,
                    courses[i].name,
                    courses[i].id
                );
            }
        }
    }

    /**
     * add markers to map for providers of given courses
     *
     * @param courses
     */
    function mapProviders(courses) {
        var providers = {};

        for (var i = 0; i < courses.length; i++) {
            if (
                courses[i] &&
                courses[i].location &&
                courses[i].location.latitude &&
                courses[i].location.longitude &&
                courses[i].provider &&
                courses[i].provider.id &&
                !providers[courses[i].provider.id]
            ) {
                providers[courses[i].provider.id] = courses[i].provider;

                addMarker(
                    courses[i].location.latitude,
                    courses[i].location.longitude,
                    courses[i].provider.name,
                    courses[i].provider.uri
                );
            }
        }
    }

    /**
     * add markers to map for unique  locations of given courses
     *
     * @param courses
     */
    function mapLocations(courses) {
        var locations = {};

        for (var i = 0; i < courses.length; i++) {
            if (
                courses[i] &&
                courses[i].location &&
                courses[i].location.id &&
                courses[i].location.latitude &&
                courses[i].location.longitude &&
                courses[i].provider &&
                courses[i].provider.id &&
                !locations[courses[i].location.id]
            ) {
                // keep track of previously mapped locations, don't double up
                locations[courses[i].location.id] = courses[i].location.id;

                addMarker(
                    courses[i].location.latitude,
                    courses[i].location.longitude,
                    createOverlayContent(courses[i]),
                    courses[i].location.id
                );
            }
        }
    }

    function createOverlayContent(course) {
        var content = '';

        content += '<div class="ui header">' + course.provider.name;

        if (course.location.street_address1) {
            content += '<div class="sub header">';

            if (course.location.neighborhood) {
                content += '<strong>' + course.location.neighborhood + '</strong><br>';
            }

            content += course.location.street_address1;

            if (course.location.street_address2) {
                content += ', ' + course.location.street_address2;
            }

            content += '</div>';
        }

        content += '</div>';

        if (CH.courseCountsByLocation &&
            CH.courseCountsByLocation[course.location.id]
        ) {
            var courseCount = CH.courseCountsByLocation[course.location.id];
            var maybePluralized = courseCount > 1 ? 'Classes' : 'Class';
            content += '<div class="map-overlay-count">' + courseCount + ' ' + maybePluralized + '</div>';
        }

        return content;
    }

    function clearMarkers() {
        for (var i = 0; i < markers.length; i++) {
            markers[i].marker.setMap(null);
        }
        markers = [];
    }

    function clearOverlays() {
        for (var i = 0; i < overlays.length; i++) {
            overlays[i].overlay.setMap(null);
        }
        overlays = [];
    }


    /**
     * adds a "marker" to the map
     *
     * markers will "pin" to a location
     * markers will show/hide an overlay when the user mouses over the pin
     * clicks on a marker trigger user-defined behavior via a callback
     *
     * @param lat
     * @param lng
     * @param content
     * @param recordId
     * @param isDropped
     */
    function addMarker(lat, lng, content, recordId, isDropped) {
        var marker;
        var overlay;

        isDropped = typeof isDropped !== 'undefined' ? isDropped : false;

        marker = new google.maps.Marker({
            position: {
                lat: lat,
                lng: lng
            },
            animation: isDropped
                ? google.maps.Animation.DROP
                : null,
            map: map
        });

        overlay = addOverlay(
            lat,
            lng,
            content,
            recordId
        );

        marker.addListener('mouseover', function() {
            trackEvent('marker hover');
            overlay.show();
        });

        marker.addListener('click', function() {
            trackEvent('marker click');
            // trigger user defined click cb
            options.markerClickCallback.call(this, recordId);
        });

        marker.addListener('mouseout', function() {
            overlay.hide();
        });

        markers.push({
            marker: marker,
            recordId: recordId
        });
    }

    /**
     * Adds a custom "overlay" popup to the map
     *
     * @param lat
     * @param lng
     * @param content
     * @param recordId
     */
    function addOverlay(lat, lng, content, recordId) {
        var overlay = new OverlayPopup(
            new google.maps.LatLng(
                lat,
                lng
            ),
            content,
            false // do not auto display
        );

        overlay.setMap(map);

        overlay.addListener('mouseout', function () {
            overlay.setInactive();
            overlay.hide();
        });

        // triggers user defined click cb
        overlay.addListener('click', function() {
            trackEvent('overlay click');
            options.overlayClickCallback.call(this, recordId);
        });

        overlays.push({
            recordId: recordId,
            overlay: overlay
        });

        return overlay;
    }

    /**
     * determine the radius of the map in km for searching within a bound
     *
     * @param   bounds      google map bounds object
     * @returns {number}    radius of map in km
     */
    function getBoundsRadius(bounds){
        var dCenterToNE = google.maps.geometry.spherical
                            .computeDistanceBetween(bounds.getCenter(), bounds.getNorthEast());

        // Let's do some trigonometry, shall we?
        // This only works for 45-45-90 Triangles, which we have since the map is rendered as a square
        // This computes the distance from the center to the eastern edge (consequently it also computes
        // the distance from the NE corner to the middle of the eastern edge.)
        // Since (a ** 2) + (b ** 2) == (c ** 2), and a == b in this scenario:
        // 2 * (a ** 2) == (c ** 2), and we know our hypotenuse (center to NE corner)
        // a == (c / Math.sqrt(2))
        // And we then divide by 1000 to convert m to km
        return (dCenterToNE / Math.sqrt(2)) / 1000;
    }

    /**
     * triggers user-provided "map change callback" function
     *
     * @param map
     */
    function triggerMapChangeCallback() {
        var center;
        if (options.mapChangeCallback && typeof options.mapChangeCallback === 'function') {
            center = map.getCenter();

            options.mapChangeCallback({
                lat: center.lat(),
                lng: center.lng(),
                radius: getBoundsRadius(
                    map.getBounds()
                ),
                zoom: map.getZoom()
            });
        }
    }

    /**
     * get current map instance
     */
    function getMap() {
        return map;
    }

    /**
     * set up map event listeners for when a user changes the map bounds (drag or zoom)
     *
     * @param map
     */
    // @todo - debounce map re-submits?

    function addMapListeners() {
        map.addListener('dragend', function(e) {
            // wait for next "idle" event
            google.maps.event.addListenerOnce(map, 'idle', function() {
                trackEvent('drag');
                triggerMapChangeCallback();
            });
        });

        map.addListener('zoom_changed', function() {
            // wait for next "idle" event
            google.maps.event.addListenerOnce(map, 'idle', function() {
                trackEvent('zoom');
                triggerMapChangeCallback();
            });
        });
    }

    /**
     * remove all map event listeners
     *
     * @param map
     */
    function removeMapListeners() {
        google.maps.event.clearInstanceListeners(map);
    }

    /**
     * initialize the map (main user entry point)
     *
     * creates the map in the specified element
     * then sets up all listeners
     * and loads any initial map data
     *
     * @param mapContainerElement       a Dom element
     * @param userOptions               user-defined options for the map
     * @param data                      optionally, set default data for the map
     */
    function initMap(mapContainerElement, userOptions, data) {
        options = Object.assign(options, userOptions);

        map = new google.maps.Map(mapContainerElement, {
            center: options.defaultCenter,
            zoom: options.defaultZoomLevel
        });

        addMapListeners();

        // optionally set initial data for the map
        if (data && Array.isArray(data)) {
            refreshData(data);
            fitMapToBounds();
        }

        $(mapContainerElement).on('click', '.gm-fullscreen-control', function() {
            trackEvent('full screen');
        })
    }

    /**
     * Set the map zoom and bounds based on all markers and overlays currently in display
     * Will not trigger any events
     */
    function fitMapToBounds() {
        var bounds = new google.maps.LatLngBounds();
        var minimumZoomLevel = 18;

        markers.forEach(function(marker) {
            bounds.extend(marker.marker.getPosition());
        });

        overlays.forEach(function(overlay) {
            bounds.extend(overlay.overlay.position);
        });

        // don't bother re-bounding if the new boundary matches the existing map bounds
        if (!bounds.equals(map.getBounds())) {
            // prevent map changes from triggering anything
            removeMapListeners();

            // add all map listeners back after the "bound fit" completes
            google.maps.event.addListenerOnce(map, 'bounds_changed', function() {
                // don't let a map auto-zoom in too close
                if (this.getZoom() > minimumZoomLevel) {
                    this.setZoom(minimumZoomLevel);
                }
                addMapListeners();
            });

            // make the map fit the boundaries with a little padding (in px)
            // so that pins labels don't get cut off
            map.fitBounds(bounds, 50);
        };
    }

    /**
     * set map center and zoom
     *
     * @todo - prevent map callbacks. currently you can only click back to the last value once
     *
     * @param lat
     * @param lng
     * @param zoom
     */
    function setCenterAndZoom(lat, lng, zoom) {
        var currentMapCenter = map.getCenter();
        var newMapCenter = lat && lng
            ? {
                lat: parseFloat(lat),
                lng: parseFloat(lng)
            } : options.defaultCenter;
        var newZoom = zoom
            ? parseFloat(zoom)
            : parseFloat(options.defaultZoomLevel);

        // if the map bounds/zoom have changed
        if (
            !currentMapCenter.equals(new google.maps.LatLng(newMapCenter)) ||
            map.getZoom() != newZoom
        ) {
            map.setCenter(newMapCenter);
            map.setZoom(newZoom);
        }
    }

    /**
     * Wipes currently displayed map data and refreshes with new data
     * Handles rendering of markers, overlays, etc
     *
     * @param data      an array of courses
     */
    function refreshData(courses) {
        clearMarkers();
        clearOverlays();

        mapLocations(courses);

        if (!courses.length) {
            trackEvent('no pins', true);
        }
    }

    /**
     * public map interface
     */
    return {
        init: initMap,
        getMap: getMap,
        refreshData: refreshData,
        fit: fitMapToBounds,
        setCenterAndZoom: setCenterAndZoom,
        // @todo - we may not need to expose all these methods
        activateMarkerForRecord: activateMarkerForRecord,
        deactivateMarkerForRecord: deactivateMarkerForRecord,
        activateOverlayForRecord: activateOverlayForRecord,
        deactivateOverlayForRecord: deactivateOverlayForRecord,
        showOverlayForRecord: showOverlayForRecord,
        hideOverlayForRecord:  hideOverlayForRecord
    }

});

